import React, { useState, useEffect } from 'react';
import { generateEventSlug } from '../utils/eventUtils';

const EventDetails = ({ event, onClose, theme = {} }) => {
    // Default theme values if not provided
    const defaultTheme = {
        text: 'text-gray-900',
        secondaryText: 'text-gray-600',
        link: 'text-blue-600 hover:text-blue-800',
        tag: 'bg-blue-100 text-blue-800',
        border: 'border-gray-200'
    };

    // Merge provided theme with defaults
    const currentTheme = { ...defaultTheme, ...theme };

    // Starred events state
    const [isStarred, setIsStarred] = useState(false);
    const eventSlug = generateEventSlug(event);

    // Check if this event is starred on mount
    useEffect(() => {
        const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
        setIsStarred(starred.includes(eventSlug));
    }, [eventSlug]);

    // Toggle star
    const handleToggleStar = () => {
        let starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
        if (starred.includes(eventSlug)) {
            starred = starred.filter(slug => slug !== eventSlug);
            setIsStarred(false);
        } else {
            starred.push(eventSlug);
            setIsStarred(true);
        }
        localStorage.setItem('starredEvents', JSON.stringify(starred));
    };

    const handleShare = () => {
        const slug = generateEventSlug(event);
        const url = `${window.location.origin}/#${slug}`;
        navigator.clipboard.writeText('Check out this event I found on ATLTech.events: ' + url);
    };

    return (
        <div className="p-4 items-center">
            <div className="flex items-center justify-between mb-2">
                <div>
                    {event.eventType === 'Featured' && (
                        <p className="bg-yellow-400 text-black text-xs px-2 py-1 font-bold rounded-full mb-2 inline-block">Featured Event</p>
                    )}
                </div>
                <button
                    onClick={handleToggleStar}
                    aria-label={isStarred ? 'Unfavorite this event' : 'Favorite this event'}
                    className="text-red-500 text-xl focus:outline-none hover:scale-110 transition-transform"
                    style={{ minWidth: 32 }}
                >
                    <i className={isStarred ? 'fas fa-heart' : 'far fa-heart'} aria-hidden="true"></i>
                </button>
            </div>
            <h2 className="text-lg font-bold mb-4">{event.title}</h2>
            <p className="mb-4 max-h-32 overflow-y-auto">{event.description}</p>
            <p className="mb-4"><strong>Organization:</strong> {event.organization}</p>
            <p className="mb-4"><strong>Date:</strong> {new Date(event.startDate).toLocaleString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            {(new Date(event.startDate).getHours() !== 0 || new Date(event.startDate).getMinutes() !== 0) && (
                <p className="mb-4"><strong>Time:</strong> {new Date(event.startDate).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })} to {new Date(event.endDate).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
            )}
            {event.location && (
                <p className="mb-4"><strong>Location:</strong> {event.location}</p>
            )}
            {event.tags && event.tags.length > 0 && (
                <div className="mb-4 text-center">
                    <div className="flex flex-wrap gap-2 mt-2 justify-center">
                        {event.tags.map((tag, index) => (
                            <span 
                                key={index} 
                                className={`${currentTheme.tag} text-xs font-medium px-2.5 py-0.5 rounded`}
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <div className="text-center py-3">
                {event.eventURL && new Date(event.endDate || event.startDate) >= new Date(new Date().setHours(0, 0, 0, 0)) && (
                    <a 
                        href={event.eventURL} 
                        className={`inline-block ${currentTheme.button} py-2 px-4 rounded transition-colors`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                    >
                        {event.eventURL === '/private-events' ? 'About private events' : 'Go to event page'}
                    </a>
                )}
            </div>
            {event.sponsorLogo && (
                <div className="mt-2 text-center mb-2">
                    <span className={`text-xs ${currentTheme.secondaryText}`}>Event Hosted By</span>
                    <a href={event.sponsorURL} target="_blank" rel="noopener noreferrer" className="block mt-1">
                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                    </a>
                </div>
            )}

            <div className={`block text-center px-4 py-2 border-t ${currentTheme.border}`}>
                <button
                    onClick={handleShare}
                    className={`${currentTheme.link} text-sm text-center gap-2`}
                >
                    <i className="fas fa-share-alt"></i>&nbsp;
                    Share This (Copy to Clipboard)
                </button>
            </div>
        </div>
    );
};

export default EventDetails; 